import { SpecificFilterOption, SpecificFilterRow } from 'modules/search/types';

import {
  ReferenceLabelsFilterBodyWithDictionary,
  getRegisterClassesAndLabelCustom,
  RegisterClassesAndSubclassesFilterBody,
  RegisterNameRowBody,
  getEadNodeUnitRelationFilterBody,
} from './filter-bodies';
import { SearchDictionaryVariant } from 'modules/search/context/dictionary-context';
import { AutocompleteOption, Filter, Params, Result } from 'types';
import {
  formatOriginatorIdentifiers,
  formatReferenceNameAndValueRow,
} from 'modules/search/v3/search-v3-format-filters';
import { formatComplexDateFilter } from 'modules/search/v3/filter-formatters/search-v3-format-complex-date';
import { SearchEntityEnum } from 'modules/search/v3/search-v3-types';
import { SearchTextOperations } from 'modules/search/form-components/search-select-text-operation';

import {
  formatRegisterClassAndLabelFilter,
  formatRegisterClassAndLabelFilter__NEW,
  formatRegisterClassAndLabelFilterEadNodeUnitOriginator,
  formatRegisterClassAndLabelFilterCollectionOriginator,
  formatRegisterClassAndLabelFilterArchivalAidOriginator,
  formatRegisterClassAndLabelFilterArchivalAidAuthor,
  formatRegisterClassAndLabelRelationsFilters,
} from 'modules/search/v3/filter-formatters/search-v3-format-class-and-label';
import fetch from 'utils/fetch';
import { SortOrder } from 'enums';
import { OriginatorReference } from 'types';
import { registerClassesOptions } from './filter-register-classes-options';

const getRemoteSourceApiCall = <T extends AutocompleteOption>({
  url,
  field,
  labelMapper,
  getParams,
}: {
  url: string;
  field: string;
  labelMapper: (o: T) => string;
  getParams?: (query: string) => Params;
}) => {
  const onChangeApiCall = async (query: string) => {
    if (!query) {
      return [];
    }
    const params: Params = getParams
      ? getParams(query)
      : {
          filters: [
            {
              field,
              value: query,
              operation: 'CONTAINS',
            },
          ],
          sort: [{ field: 'name', order: SortOrder.ASC }],
          size: 50,
        };

    const body = JSON.stringify(params);
    const response = await fetch(url, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body,
    });
    const json = (await response.json()) as Result<T>;
    return (json?.items ?? []).map(labelMapper);
  };
  return onChangeApiCall;
};

const collectionsOptions = [
  {
    id: 'fulltextExcludedLabel',
    label: 'Název',
    remoteSource: {
      onChangeApiCall: getRemoteSourceApiCall({
        url: '/api/archival-collection/list/autocomplete',
        field: 'name',
        labelMapper: (o) => o.autoCompleteLabel ?? '',
      }),
    },
  },

  {
    id: 'timeRange',
    label: 'Časový rozsah',
  },
  {
    id: 'thematicDescription',
    label: 'Tématický popis',
  },
  // {
  //   id: 'originators.label',
  //   label: 'Původce fondu',
  //   remoteSource: {
  //     onChangeApiCall: getRemoteSourceApiCall<Originator & AutocompleteOption>({
  //       url: '/api/originator/list',
  //       field: 'label',
  //       labelMapper: (o) => o.label ?? '',
  //       getParams: (value) => {
  //         return {
  //           size: 50,
  //           sort: [{ field: 'label', order: SortOrder.ASC }],
  //           filters: [
  //             {
  //               field: 'label',
  //               value,
  //               operation: 'START_WITH',
  //             },
  //             {
  //               field: 'hasArchivalCollections',
  //               value: true,
  //               operation: 'EQ',
  //             },
  //           ],
  //         };
  //       },
  //     }),
  //   },
  // },
  {
    id: 'originators.label-2',
    label: 'Původce',
    filterType: 'CUSTOM',
    getCustomFieldValueLabel: (value: AutocompleteOption) => value?.label ?? '',
    FilterBodyComponent: getRegisterClassesAndLabelCustom({
      classesSelectOptions: registerClassesOptions.filter((c) =>
        ['_ALL_CLASSES', 'PERSON', 'PARTY_GROUP', 'DYNASTY', 'EVENT'].includes(
          c.id
        )
      ),
      defaultPreFilters: [
        { field: 'hasArchivalCollections', value: true, operation: 'EQ' },
        {
          operation: 'OR',
          filters: ['PERSON', 'PARTY_GROUP', 'DYNASTY', 'EVENT'].map(
            (value) => ({
              operation: 'EQ',
              field: 'originatorClass',
              value,
            })
          ),
        },
      ],
    }),

    filterFormatter: formatRegisterClassAndLabelFilterCollectionOriginator as (
      row: SpecificFilterRow
    ) => Filter | undefined,
  },
  {
    id: 'evidenceNumber',
    ids: ['evidenceNumber', 'archivalCollection.evidenceNumber'],
    label: 'Číslo ev. listu NAD',
    defaultTextOperation: SearchTextOperations.EQ,
  },
  {
    id: 'languages',
    label: 'Jazyky',
    sourceKey: SearchDictionaryVariant.ARCHIVAL_COLLECTION_LANGUAGES,
  },
  {
    id: 'accessibility',
    label: 'Přístupnost',
    sourceKey: SearchDictionaryVariant.COLLECTION_ACCESSIBILITY,
  },
  {
    id: 'groups',
    label: 'Věcná skupina',
    sourceKey: SearchDictionaryVariant.COLLECTION_GROUPS,
  },
];

const aidOptions = [
  {
    id: 'fulltextExcludedLabel',
    label: 'Název',
    remoteSource: {
      onChangeApiCall: getRemoteSourceApiCall<AutocompleteOption>({
        url: '/api/archival-aid-metadata/list/autocomplete',
        field: 'name',
        labelMapper: (o) => o.autoCompleteLabel ?? '',
      }),
    },
  },
  {
    id: 'timeRange',
    label: 'Časový rozsah',
  },
  // {
  //   id: 'search.label',
  //   label: 'Původce',
  //   remoteSource: {
  //     onChangeApiCall: getRemoteSourceApiCall<Originator & AutocompleteOption>({
  //       url: '/api/originator/list',
  //       field: 'label',
  //       labelMapper: (o) => o.label ?? '',
  //       getParams: (value) => {
  //         return {
  //           size: 50,
  //           sort: [{ field: 'label', order: SortOrder.ASC }],
  //           filters: [
  //             {
  //               field: 'label',
  //               value,
  //               operation: 'START_WITH',
  //             },
  //             {
  //               field: 'hasArchivalAids',
  //               value: true,
  //               operation: 'EQ',
  //             },
  //           ],
  //         };
  //       },
  //     }),
  //   },
  // },
  {
    id: 'search.label-2',
    label: 'Původce',
    filterType: 'CUSTOM',
    getCustomFieldValueLabel: (value: AutocompleteOption) => value?.label ?? '',
    FilterBodyComponent: getRegisterClassesAndLabelCustom({
      classesSelectOptions: registerClassesOptions.filter((c) =>
        ['_ALL_CLASSES', 'PERSON', 'PARTY_GROUP', 'DYNASTY', 'EVENT'].includes(
          c.id
        )
      ),
      defaultPreFilters: [
        { field: 'hasArchivalAids', value: true, operation: 'EQ' },
        {
          operation: 'OR',
          filters: ['PERSON', 'PARTY_GROUP', 'DYNASTY', 'EVENT'].map(
            (value) => ({
              operation: 'EQ',
              field: 'originatorClass',
              value,
            })
          ),
        },
      ],
    }),

    filterFormatter: formatRegisterClassAndLabelFilterArchivalAidOriginator as (
      row: SpecificFilterRow
    ) => Filter | undefined,
  },
  {
    id: 'evidenceNumber',
    ids: ['evidenceNumber'],
    label: 'Číslo pomůcky',
    defaultTextOperation: SearchTextOperations.EQ,
  },
  {
    id: 'eadNode.langMaterial',
    ids: ['eadNode.langMaterial', 'eadNode.majorityLangMaterial'],
    label: 'Jazyky popsaných archiválií',
    sourceKey: SearchDictionaryVariant.AIDS_LANG_MATERIALS,
  },
  {
    id: 'authors.labels-2',
    label: 'Autor',
    filterType: 'CUSTOM',
    getCustomFieldValueLabel: (value: AutocompleteOption) => value?.label ?? '',
    FilterBodyComponent: getRegisterClassesAndLabelCustom({
      classesSelectOptions: registerClassesOptions.filter((c) =>
        ['_ALL_CLASSES', 'PERSON', 'PARTY_GROUP', 'DYNASTY', 'EVENT'].includes(
          c.id
        )
      ),
      defaultPreFilters: [
        {
          operation: 'OR',
          filters: ['PERSON', 'PARTY_GROUP', 'DYNASTY', 'EVENT'].map(
            (value) => ({
              operation: 'EQ',
              field: 'originatorClass',
              value,
            })
          ),
        },
      ],
    }),

    filterFormatter: formatRegisterClassAndLabelFilterArchivalAidAuthor as (
      row: SpecificFilterRow
    ) => Filter | undefined,
  },
  {
    id: 'yearOfOrigin',
    label: 'Rok vytvoření pomůcky',
  },
  {
    id: 'type',
    label: 'Druh pomůcky',
    sourceKey: SearchDictionaryVariant.AID_TYPE,
  },
];

const archivalUnitOptions: {
  id: string;
  ids?: string[];
  label: string;
  nestedQueryEnabled?: boolean;
  sourceKey?: SearchDictionaryVariant;
  FilterBodyComponent?: (rowIndex: number, disabled?: boolean) => JSX.Element;
  filterFormatter?: SpecificFilterOption['filterFormatter'];
  filterType?: SpecificFilterOption['filterType'];
  defaultTextOperation?: SpecificFilterOption['defaultTextOperation'];
}[] = [
  {
    id: 'summary',
    ids: ['summary', 'title'],
    label: 'Obsah (regest)',
  },
  {
    id: 'timeRange',
    label: 'Datace',
  },
  {
    id: 'eadNode.formalTitle',
    label: 'Formální název jednotky popisu',
  },
  {
    id: 'eadNode.scopeContent',
    ids: ['eadNode.scopeContent', 'thematicDescription'],
    label: 'Tematický popis',
  },
  {
    ids: ['structuredLink', 'referenceId'],
    id: 'structuredLink',
    label: 'Referenční označení',
    defaultTextOperation: SearchTextOperations.MATCH_PHRASE,
  },
  {
    id: 'referenceLabels.value',
    ids: ['referenceLabels.value', 'eadNode.referenceLabels.value'], // tieto idecka sa ignoruju, pozri formatter
    label: 'Jiné označení',
    nestedQueryEnabled: false,
    sourceKey: SearchDictionaryVariant.ARCHIVAL_UNIT_IDENTIFIERS,
    FilterBodyComponent: ReferenceLabelsFilterBodyWithDictionary,
    filterFormatter: (formatReferenceNameAndValueRow as unknown) as (
      row: SpecificFilterRow
    ) => Filter | undefined,
    filterType: 'CUSTOM',
  },
  {
    id: 'eadNode.langMaterial',
    label: 'Jazyk',
    sourceKey: SearchDictionaryVariant.EAD_UNIT_LANG_MATERIALS,
  },

  {
    id: 'eadNode.scaleMaterialSpec',
    label: 'Měřítko',
  },
];

const searchInRegisterOptions = [
  {
    id: 'label',
    label: 'Název',
    FilterBodyComponent: RegisterNameRowBody,
  },

  {
    id: 'briefDescription',
    label: 'Stručná charakteristika',
  },
  {
    id: 'identifiers.value',
    label: 'Identifikátory',
    FilterBodyComponent: ReferenceLabelsFilterBodyWithDictionary,
    sourceKey: SearchDictionaryVariant.ORIGINATOR_IDENTIFIERS,
    filterFormatter: (formatOriginatorIdentifiers as unknown) as (
      row: SpecificFilterRow
    ) => Filter | undefined,
    filterType: 'CUSTOM' as SpecificFilterOption['filterType'],
  },
];

const identifiersOptions = [
  {
    ids: ['structuredLink', 'referenceId'],
    id: 'structuredLink-referenceId',
    label: 'Referenční označení',
    searchIn: ['EAD_NODE_UNIT'],
    validSearchSpace: ['EAD_NODE_UNIT'],
    defaultTextOperation: SearchTextOperations.MATCH_PHRASE,
  },
  {
    ids: ['evidenceNumber', 'archivalCollection.evidenceNumber'],
    id: 'evidenceNumber-archivalCollection.evidenceNumber',
    label: 'Číslo ev. listu NAD',
    searchIn: ['ARCHIVAL_COLLECTION'],
    // cascading off => reduce validSearchSpace
    //validSearchSpace: ['ARCHIVAL_COLLECTION', 'ARCHIVAL_AID', 'EAD_NODE_UNIT'],
    validSearchSpace: ['ARCHIVAL_COLLECTION'],
    defaultTextOperation: SearchTextOperations.EQ,
    cascading: false,

    //archivalEntityType: 'ARCHIVAL_COLLECTION',
  },
  {
    id: 'evidenceNumber',
    label: 'Číslo pomůcky',
    archivalEntityType: 'ARCHIVAL_AID',
    searchIn: ['ARCHIVAL_AID'],
    // cascading off => reduce validSearchSpace
    //validSearchSpace: ['ARCHIVAL_AID', 'EAD_NODE_UNIT'],
    validSearchSpace: ['ARCHIVAL_AID'],
    defaultTextOperation: SearchTextOperations.EQ,
    cascading: false,
  },
  {
    id: 'referenceLabels.value-eadNode.referenceLabels.value',
    ids: ['referenceLabels.value', 'eadNode.referenceLabels.value'],
    label: 'Jiné označení archiválií',
    nestedQueryEnabled: false,
    sourceKey: SearchDictionaryVariant.ARCHIVAL_UNIT_IDENTIFIERS,
    FilterBodyComponent: ReferenceLabelsFilterBodyWithDictionary,
    filterFormatter: (formatReferenceNameAndValueRow as unknown) as (
      row: SpecificFilterRow
    ) => Filter | undefined,
    searchIn: ['EAD_NODE_UNIT'],
    validSearchSpace: ['EAD_NODE_UNIT'],
    filterType: 'CUSTOM' as SpecificFilterOption['filterType'],
  },
  {
    id: 'Identifikátory rejstříkových hesel',
    ids: ['identifiers.value'], // id sa ignoruje, pozri formatter
    label: 'Identifikátory rejstříkových hesel',
    searchIn: ['ORIGINATOR'],
    validSearchSpace: ['ORIGINATOR'],
    sourceKey: SearchDictionaryVariant.ORIGINATOR_IDENTIFIERS,
    FilterBodyComponent: ReferenceLabelsFilterBodyWithDictionary,
    filterFormatter: (formatOriginatorIdentifiers as unknown) as (
      row: SpecificFilterRow
    ) => Filter | undefined,
    filterType: 'CUSTOM' as SpecificFilterOption['filterType'],
  },
];

export const createRowCategoryOptionsV3 = (): SpecificFilterOption[] => {
  const pinned: SpecificFilterOption[] = [
    {
      id: 'FTX',
      label: 'Fulltext',
      searchIn: [
        SearchEntityEnum.ARCHIVAL_AID,
        SearchEntityEnum.ARCHIVAL_COLLECTION,
        SearchEntityEnum.EAD_NODE_UNIT,
        SearchEntityEnum.ORIGINATOR,
      ],
      category: {
        id: 'PINNED',
        showLabelInSelect: false,
        validSearchSpace: [
          SearchEntityEnum.ARCHIVAL_AID,
          SearchEntityEnum.ARCHIVAL_COLLECTION,
          SearchEntityEnum.EAD_NODE_UNIT,
          SearchEntityEnum.ORIGINATOR,
        ],
      },
      filterType: 'FTX',
      cascading: false,
    },
  ];

  const searchByRegisterItemLabel: SpecificFilterOption[] = [
    {
      id: 'SEARCH-BY-REGISTER-ITEM-LABEL',
      label: 'Hledat podle rejstříkového hesla',
      labelStyle: { fontWeight: 'bold' },
      searchIn: [
        SearchEntityEnum.ARCHIVAL_AID,
        SearchEntityEnum.ARCHIVAL_COLLECTION,
        SearchEntityEnum.EAD_NODE_UNIT,
      ],
      getCustomFieldValueLabel: (value: AutocompleteOption) =>
        value?.label ?? '',
      FilterBodyComponent: getRegisterClassesAndLabelCustom({
        classesSelectOptions: registerClassesOptions,
      }),
      filterFormatter: (formatRegisterClassAndLabelFilter as unknown) as (
        row: SpecificFilterRow
      ) => Filter | undefined,
      category: {
        id: 'PINNED',

        validSearchSpace: [
          SearchEntityEnum.ARCHIVAL_AID,
          SearchEntityEnum.ARCHIVAL_COLLECTION,
          SearchEntityEnum.EAD_NODE_UNIT,
        ],
      },
      cascading: false,
      filterType: 'CUSTOM',
    },
  ];

  const nameGeneral: SpecificFilterOption[] = [
    {
      id: 'name-and-regest',
      label: 'Název / obsah',
      category: {
        id: 'PINNED',
        showLabelInSelect: false,
        validSearchSpace: [
          SearchEntityEnum.ARCHIVAL_AID,
          SearchEntityEnum.ARCHIVAL_COLLECTION,
          SearchEntityEnum.EAD_NODE_UNIT,
          SearchEntityEnum.ORIGINATOR,
        ],
      },
      cascading: false,
      filterType: 'COMPLEX_TEXT',
      targetFieldsComplex_v3: {
        EAD_NODE_UNIT: [
          'summary',
          'eadNode.formalTitle',
          'eadNode.title',
          'formalTitle',
          'title',
        ],
        ARCHIVAL_COLLECTION: ['name'],
        ARCHIVAL_AID: ['name'],
        ORIGINATOR: ['label'],
      },

      targetFieldsComplex: [
        {
          archivalEntityType: 'ARCHIVAL_UNIT',
          targetField: 'summary',
          nestedQueryEnabled: false,
        },
        {
          archivalEntityType: 'ARCHIVAL_AID',
          targetField: 'name',
          nestedQueryEnabled: false,
        },
        {
          archivalEntityType: 'ARCHIVAL_COLLECTION',
          targetField: 'name',
          nestedQueryEnabled: false,
        },
        {
          archivalEntityType: 'ARCHIVAL_UNIT',
          targetField: 'eadNode.formalTitle',
          nestedQueryEnabled: false,
        },
        {
          archivalEntityType: 'ARCHIVAL_UNIT',
          targetField: 'eadNode.title',
          nestedQueryEnabled: false,
        },
        {
          archivalEntityType: 'EAD_NODE',
          targetField: 'formalTitle',
          nestedQueryEnabled: false,
        },
        {
          archivalEntityType: 'EAD_NODE',
          targetField: 'title',
          nestedQueryEnabled: false,
        },
        {
          archivalEntityType: 'ORIGINATOR',
          targetField: 'label',
          nestedQueryEnabled: false,
        },
      ],
    },
  ];

  const originatorsGeneral: SpecificFilterOption[] = [
    {
      id: 'originators-general',
      label: 'Původce',
      FilterBodyComponent: getRegisterClassesAndLabelCustom({
        classesSelectOptions: registerClassesOptions.filter((c) =>
          [
            '_ALL_CLASSES',
            'PERSON',
            'PARTY_GROUP',
            'DYNASTY',
            'EVENT',
          ].includes(c.id)
        ),
        defaultPreFilters: [
          {
            operation: 'OR',
            filters: ['PERSON', 'PARTY_GROUP', 'DYNASTY', 'EVENT'].map(
              (value) => ({
                operation: 'EQ',
                field: 'originatorClass',
                value,
              })
            ),
          },
        ],
      }),
      category: {
        id: 'PINNED',
        showLabelInSelect: false,
        validSearchSpace: [
          SearchEntityEnum.EAD_NODE_UNIT,
          SearchEntityEnum.ARCHIVAL_AID,
          SearchEntityEnum.ARCHIVAL_COLLECTION,
        ],
      },
      filterType: 'CUSTOM',
      cascading: false,
      getCustomFieldValueLabel: (value: AutocompleteOption) =>
        value?.label ?? '',

      filterFormatter: (formatRegisterClassAndLabelFilter__NEW as unknown) as (
        row: SpecificFilterRow
      ) => Filter | undefined,

      targetFieldsComplex_v3: {
        EAD_NODE_UNIT: ['originator.label'],
        ARCHIVAL_COLLECTION: ['originators.label'],
        ARCHIVAL_AID: ['search.label'],
        ORIGINATOR: [],
      },

      targetFieldsComplex: [
        {
          archivalEntityType: 'ARCHIVAL_COLLECTION',
          targetField: 'originators.label',
          nestedQueryEnabled: false,
        },
        {
          archivalEntityType: 'ARCHIVAL_AID',
          targetField: 'search.label',
          nestedQueryEnabled: false,
        },
      ],
    },
  ];

  // const originatorsGeneral__OLD: SpecificFilterOption[] = [
  //   {
  //     id: 'originators-general',
  //     label: 'Původce',
  //     category: {
  //       id: 'PINNED',
  //       showLabelInSelect: false,
  //       validSearchSpace: [
  //         SearchEntityEnum.EAD_NODE_UNIT,
  //         SearchEntityEnum.ARCHIVAL_AID,
  //         SearchEntityEnum.ARCHIVAL_COLLECTION,
  //       ],
  //     },
  //     filterType: 'COMPLEX_TEXT',
  //     cascading: false,

  //     targetFieldsComplex_v3: {
  //       EAD_NODE_UNIT: ['originator.label'],
  //       ARCHIVAL_COLLECTION: ['originators.label'],
  //       ARCHIVAL_AID: ['search.label'],
  //       ORIGINATOR: [],
  //     },

  //     targetFieldsComplex: [
  //       {
  //         archivalEntityType: 'ARCHIVAL_COLLECTION',
  //         targetField: 'originators.label',
  //         nestedQueryEnabled: false,
  //       },
  //       {
  //         archivalEntityType: 'ARCHIVAL_AID',
  //         targetField: 'search.label',
  //         nestedQueryEnabled: false,
  //       },
  //     ],
  //   },
  // ];

  const timeRangeGeneral: SpecificFilterOption[] = [
    {
      id: 'time-range-general',
      label: 'Datace',
      category: {
        id: 'PINNED',
        showLabelInSelect: false,
        validSearchSpace: [
          SearchEntityEnum.ARCHIVAL_AID,
          SearchEntityEnum.ARCHIVAL_COLLECTION,
          SearchEntityEnum.EAD_NODE_UNIT,
          SearchEntityEnum.ORIGINATOR,
        ],
      },
      filterType: 'COMPLEX_DATE',
      filterFormatter: (formatComplexDateFilter as unknown) as (
        row: SpecificFilterRow
      ) => Filter, //TODO: typing, na params map
      cascading: false,
      targetFieldsComplex_v3: {
        EAD_NODE_UNIT: ['timeRange'],
        ARCHIVAL_COLLECTION: ['timeRange'],
        ARCHIVAL_AID: ['timeRange'],
        ORIGINATOR: ['originDate '],
      },

      targetFieldsComplex: [
        {
          archivalEntityType: 'ARCHIVAL_COLLECTION',
          targetField: 'timeRange',
        },
        {
          archivalEntityType: 'ARCHIVAL_UNIT',
          targetField: 'timeRange',
        },
        {
          archivalEntityType: 'ARCHIVAL_AID',
          targetField: 'timeRange',
        },

        {
          archivalEntityType: 'ORIGINATOR',
          targetField: 'chronologicalSupplement',
        },
      ],
    },
  ];

  const descriptionGeneral: SpecificFilterOption[] = [
    {
      id: 'description-general',
      label: 'Tematický popis / stručná charakteristika',
      category: {
        id: 'PINNED',
        showLabelInSelect: false,
        validSearchSpace: [
          SearchEntityEnum.ARCHIVAL_AID,
          SearchEntityEnum.ARCHIVAL_COLLECTION,
          SearchEntityEnum.EAD_NODE_UNIT,
          SearchEntityEnum.ORIGINATOR,
        ],
      },
      filterType: 'COMPLEX_TEXT',
      cascading: false,
      targetFieldsComplex_v3: {
        EAD_NODE_UNIT: ['scopeContent', 'eadNode.scopeContent'],
        ARCHIVAL_COLLECTION: ['thematicDescription'],
        ARCHIVAL_AID: ['scopeContent', 'eadNode.scopeContent'],
        ORIGINATOR: ['briefDescription'],
      },
      targetFieldsComplex: [
        {
          archivalEntityType: 'ARCHIVAL_COLLECTION',
          targetField: 'thematicDescription',
        },
        {
          archivalEntityType: 'ARCHIVAL_AID',
          targetField: 'eadNode.scopeContent',
          nestedQueryEnabled: false,
        },
        {
          archivalEntityType: 'ARCHIVAL_UNIT',
          targetField: 'eadNode.scopeContent',
          nestedQueryEnabled: false,
        },

        {
          archivalEntityType: 'ORIGINATOR',
          targetField: 'briefDescription',
        },
      ],
    },
  ];

  const identifiersGeneral: SpecificFilterOption[] = identifiersOptions.map(
    (o) => ({
      id: `identifiers-${o.id}`,
      label: o.label,
      targetField: o.id,
      targetFields: o.ids ?? [o.id],
      nestedQueryEnabled: o.nestedQueryEnabled,
      searchIn: o.searchIn,
      cascading: o.cascading ?? false,
      filterFormatter: o?.filterFormatter,
      FilterBodyComponent: o?.FilterBodyComponent,
      sourceKey: o?.sourceKey,
      category: {
        label: 'Identifikátory',
        id: 'IDENTIFIERS_GENERAL',
        showLabelInSelect: true,
        validSearchSpace: o.validSearchSpace as SearchEntityEnum[],
      },
      filterType: o.filterType ?? 'TEXT',
      defaultTextOperation: o.defaultTextOperation,
    })
  );

  //TODO: treba preriedit na zaklade checkboxov fondy , popis, ...atd

  const lanugagesGeneral: SpecificFilterOption[] = [
    {
      id: `languages-general-1`,
      label: 'Převažující jazyky',
      cascading: false,
      // TODO: aj ostatne source keys dodat - tie nove
      // a tiez search space a pod asi poriesit
      sourceKey: SearchDictionaryVariant.PREDOMINANT_LANGUAGES,
      targetFieldsComplex_v3: {
        EAD_NODE_UNIT: [],
        ARCHIVAL_COLLECTION: ['languages'],
        ARCHIVAL_AID: ['eadNode.majorityLangMaterial'],
        ORIGINATOR: [],
      },
      targetFieldsComplex: [
        {
          archivalEntityType: 'ARCHIVAL_COLLECTION',
          targetField: 'languages',
          nestedQueryEnabled: false,
        },
        {
          archivalEntityType: 'ARCHIVAL_AID',
          targetField: 'eadNode.majorityLangMaterial',
          nestedQueryEnabled: false,
        },
      ],
      category: {
        label: 'Jazyk',
        id: 'LANGUAGES_GENERAL',
        showLabelInSelect: true,

        validSearchSpace: [
          SearchEntityEnum.ARCHIVAL_AID,
          SearchEntityEnum.ARCHIVAL_COLLECTION,
        ],
      },
      filterType: 'COMPLEX_TEXT',
    },
    {
      id: `languages-general-2`,
      label: 'Jazyk archiválie',
      cascading: false,
      sourceKey: SearchDictionaryVariant.EAD_UNIT_LANG_MATERIALS,
      targetFieldsComplex_v3: {
        EAD_NODE_UNIT: ['langMaterial', 'eadNode.langMaterial'],
        ARCHIVAL_COLLECTION: [],
        ARCHIVAL_AID: [],
        ORIGINATOR: [],
      },
      targetFieldsComplex: [
        {
          archivalEntityType: 'ARCHIVAL_UNIT',
          targetField: 'langMaterial',
          nestedQueryEnabled: false,
        },
        {
          archivalEntityType: 'ARCHIVAL_UNIT',
          targetField: 'eadNode.langMaterial',
          nestedQueryEnabled: false,
        },
        {
          archivalEntityType: 'ARCHIVAL_AID',
          targetField: 'eadNode.langMaterial',
          nestedQueryEnabled: false,
        },
        {
          archivalEntityType: 'ARCHIVAL_COLLECTION',
          targetField: 'languages',
          nestedQueryEnabled: false,
        },
      ],
      category: {
        label: 'Jazyk',
        id: 'LANGUAGES_GENERAL',
        showLabelInSelect: true,
        validSearchSpace: [
          //SearchEntityEnum.ARCHIVAL_AID,
          SearchEntityEnum.EAD_NODE_UNIT,
          //SearchEntityEnum.ORIGINATOR,
        ],
      },
      filterType: 'COMPLEX_TEXT',
    },
  ];

  const formattedCollectionOptions: SpecificFilterOption[] = collectionsOptions.map(
    (o) => ({
      id: `collections-${o.id}`,
      label: o.label,
      targetField: o.id,
      targetFields: o.ids ?? [o.id],
      sourceKey: o.sourceKey,
      remoteSource: o.remoteSource,
      getCustomFieldValueLabel: (value: AutocompleteOption) =>
        value?.label ?? '',
      FilterBodyComponent: o?.FilterBodyComponent,
      filterFormatter: o?.filterFormatter,
      searchIn: ['ARCHIVAL_COLLECTION'],
      cascading: false,
      category: {
        label: 'Fondy',
        id: 'COLLECTIONS',
        showLabelInSelect: true,
        validSearchSpace: [
          SearchEntityEnum.ARCHIVAL_COLLECTION,
          // cascading off => reduce validSearchSpace
          //SearchEntityEnum.ARCHIVAL_AID,
          //SearchEntityEnum.EAD_NODE_UNIT,
        ],
      },
      filterType: (o.filterType ??
        (o.id === 'timeRange'
          ? 'TIME_RANGE'
          : 'TEXT')) as SpecificFilterOption['filterType'],
      defaultTextOperation: o.defaultTextOperation,
    })
  );

  const formattedAidsOptions: SpecificFilterOption[] = aidOptions.map((o) => ({
    id: `aids-${o.id}`,
    label: o.label,
    targetField: o.id ?? '',
    targetFields: o.ids ?? [o.id],
    sourceKey: o.sourceKey,
    remoteSource: o.remoteSource,
    getCustomFieldValueLabel: (value: AutocompleteOption) => value?.label ?? '',
    FilterBodyComponent: o?.FilterBodyComponent,
    filterFormatter: o?.filterFormatter,
    searchIn: ['ARCHIVAL_AID'],
    cascading: false,
    category: {
      label: 'Archivní pomůcky',
      id: 'AID',
      showLabelInSelect: true,
      validSearchSpace: [
        SearchEntityEnum.ARCHIVAL_AID,
        // cascading off => reduce validSearchSpace
        //SearchEntityEnum.EAD_NODE_UNIT,
      ],
    },

    filterType: (o.filterType ??
      (o.id === 'timeRange'
        ? 'TIME_RANGE'
        : 'TEXT')) as SpecificFilterOption['filterType'],
    defaultTextOperation: o.defaultTextOperation,
  }));

  const formattedArchivalUnitsOptions: SpecificFilterOption[] = [
    ...archivalUnitOptions.map((o) => ({
      id: `archivalUnits-${o.id}`,
      label: o.label,
      targetField: o.id,
      targetFields: o.ids ?? [o.id],
      nestedQueryEnabled: o.nestedQueryEnabled,
      FilterBodyComponent: o?.FilterBodyComponent,
      filterFormatter: o?.filterFormatter,
      sourceKey: o?.sourceKey,
      searchIn: ['EAD_NODE_UNIT'],
      filterType:
        o?.filterType ?? (o.id === 'timeRange' ? 'TIME_RANGE' : 'TEXT'),
      category: {
        label: 'Archivní popis / archiválie',
        id: 'ARCHIVAL_UNITS',
        showLabelInSelect: true,
        validSearchSpace: [SearchEntityEnum.EAD_NODE_UNIT],
      },
      defaultTextOperation: o.defaultTextOperation,
    })),
    {
      id: 'archivalUnits-originators',
      label: 'Původce',
      searchIn: ['EAD_NODE_UNIT'],
      category: {
        label: 'Archivní popis / archiválie',
        id: 'ARCHIVAL_UNITS',
        showLabelInSelect: true,
        validSearchSpace: [SearchEntityEnum.EAD_NODE_UNIT],
      },
      filterType: 'CUSTOM',
      cascading: false,
      getCustomFieldValueLabel: (value: AutocompleteOption) =>
        value?.label ?? '',
      FilterBodyComponent: getRegisterClassesAndLabelCustom({
        classesSelectOptions: registerClassesOptions.filter((c) =>
          [
            '_ALL_CLASSES',
            'PERSON',
            'PARTY_GROUP',
            'DYNASTY',
            'EVENT',
          ].includes(c.id)
        ),
        defaultPreFilters: [
          {
            operation: 'OR',
            filters: ['PERSON', 'PARTY_GROUP', 'DYNASTY', 'EVENT'].map(
              (value) => ({
                operation: 'EQ',
                field: 'originatorClass',
                value,
              })
            ),
          },
        ],
      }),

      filterFormatter: formatRegisterClassAndLabelFilterEadNodeUnitOriginator as (
        row: SpecificFilterRow
      ) => Filter | undefined,
    },
    {
      searchIn: ['EAD_NODE_UNIT'],
      id: `archivalUnits-roles`,
      label: 'Rejstříkové heslo v roli',

      targetField: '',
      category: {
        label: 'Archivní popis / archiválie',
        id: 'ARCHIVAL_UNITS',
        showLabelInSelect: true,
        validSearchSpace: [SearchEntityEnum.EAD_NODE_UNIT],
      },
      filterType: 'CUSTOM',
      getCustomFieldValueLabel: (value: OriginatorReference) =>
        value.label ?? '',
      FilterBodyComponent: getEadNodeUnitRelationFilterBody({
        classesSelectOptions: registerClassesOptions,
        // classesSelectOptions: registerClassesOptions.filter((c) =>
        //   [
        //     '_ALL_CLASSES',
        //     'PERSON',
        //     'PARTY_GROUP',
        //     'DYNASTY',
        //     'EVENT',
        //   ].includes(c.id)
        // ),
        defaultPreFilters: [],
        // defaultPreFilters: [
        //   {
        //     operation: 'OR',
        //     filters: ['PERSON', 'PARTY_GROUP', 'DYNASTY', 'EVENT'].map(
        //       (value) => ({
        //         operation: 'EQ',
        //         field: 'originatorClass',
        //         value,
        //       })
        //     ),
        //   },
        // ],
      }),

      filterFormatter: formatRegisterClassAndLabelRelationsFilters as (
        row: SpecificFilterRow
      ) => Filter | undefined,
    },
  ] as SpecificFilterOption[];

  const formattedRegisterOptions: SpecificFilterOption[] = searchInRegisterOptions.map(
    (o) => ({
      id: `registers-${o.id}`,
      label: o.label,
      targetField: o.id,
      targetFields: [o.id],
      FilterBodyComponent: o?.FilterBodyComponent,
      filterFormatter: o?.filterFormatter,
      sourceKey: o?.sourceKey,
      searchIn: ['ORIGINATOR'],
      category: {
        label: 'Rejstříkové heslo',
        id: 'REGISTERS',
        showLabelInSelect: true,
        validSearchSpace: [SearchEntityEnum.ORIGINATOR],
      },
      filterType:
        o.filterType ?? (o.id === 'timeRange' ? 'TIME_RANGE' : 'TEXT'),
    })
  );

  const formattedRegisterClasses: SpecificFilterOption[] = [
    {
      // disabled: true,
      id: `register-classes-and-subclasses`,
      label: 'Třída a podtřída',
      FilterBodyComponent: RegisterClassesAndSubclassesFilterBody,
      searchIn: ['ORIGINATOR'],
      category: {
        label: 'Rejstříkové heslo',
        id: 'REGISTERS',
        showLabelInSelect: true,
        validSearchSpace: [SearchEntityEnum.ORIGINATOR],
      },
      //targetRegisterClass: { id: o.id, label: o.label },
      filterType: 'REGISTER_CLASSES',
    },
  ];

  const formattedRelationWithClassOptions: SpecificFilterOption[] = [
    {
      id: `relation-to-entity`,
      label: 'Vztah k jinému heslu',
      searchIn: ['ORIGINATOR'],
      category: {
        label: 'Rejstříkové heslo',
        id: 'REGISTERS',
        showLabelInSelect: true,
        validSearchSpace: [SearchEntityEnum.ORIGINATOR],
      },
      //targetRegisterClass: { id: o.id, label: o.label },
      filterType: 'RELATION_V2',
    },
  ];

  return [
    ...pinned,
    ...searchByRegisterItemLabel,
    ...nameGeneral,
    ...originatorsGeneral,
    ...timeRangeGeneral,
    ...descriptionGeneral,
    ...identifiersGeneral,
    ...lanugagesGeneral,
    ...formattedCollectionOptions,
    ...formattedAidsOptions,
    ...formattedArchivalUnitsOptions,
    ...formattedRegisterOptions,
    ...formattedRegisterClasses,
    ...formattedRelationWithClassOptions,
  ];
};
